import Hls, {
  HlsConfig,
  LoaderContext,
  FragmentLoaderContext,
  Loader,
  LoaderConfiguration,
  LoaderCallbacks,
  LoaderStats,
} from 'hls.js';

export class CustomFragmentLoader implements Loader<FragmentLoaderContext> {
  private defaultLoader: Loader<LoaderContext>;
  private static loadedSegments: Map<string, Set<number>> = new Map([
    ['main', new Set()],
    ['audio', new Set()],
  ]);

  public context!: FragmentLoaderContext;
  public stats: LoaderStats;

  constructor(config: HlsConfig) {
    this.defaultLoader = new (Hls.DefaultConfig.loader as new (
      config: HlsConfig,
    ) => Loader<LoaderContext>)(config);
    this.stats = {
      aborted: false,
      loaded: 0,
      retry: 0,
      total: 0,
      chunkCount: 0,
      bwEstimate: 0,
      loading: { start: 0, first: 0, end: 0 },
      parsing: { start: 0, end: 0 },
      buffering: { start: 0, first: 0, end: 0 },
    };
  }

  destroy(): void {
    this.defaultLoader.destroy();
  }

  abort(): void {
    this.defaultLoader.abort();
  }

  load(
    context: FragmentLoaderContext,
    config: LoaderConfiguration,
    callbacks: LoaderCallbacks<FragmentLoaderContext>,
  ): void | Promise<void> {
    this.context = context;

    const originalOnProgress = callbacks.onProgress;
    const modifiedCallbacks: LoaderCallbacks<FragmentLoaderContext> = {
      ...callbacks,
      onProgress: (
        stats: LoaderStats,
        contextFragment: FragmentLoaderContext,
        data: string | ArrayBuffer,
      ) => {
        this.stats = stats;

        if (
          contextFragment.frag &&
          contextFragment.frag.sn !== 'initSegment'
        ) {
          const fragType = contextFragment.frag.type;
          const segments = CustomFragmentLoader.loadedSegments.get(fragType);
          segments?.add(contextFragment.frag.sn as number);
        }

        if (originalOnProgress) {
          originalOnProgress(stats, contextFragment, data, {});
        }
      },
    };

    return this.defaultLoader.load(
      context as LoaderContext,
      config,
      modifiedCallbacks as LoaderCallbacks<LoaderContext>,
    );
  }

  static canHaveQualityLevels(): boolean {
    return true;
  }

  public static cleanupLoadedSegments(validSegments: Set<number>) {
    for (const [, segments] of CustomFragmentLoader.loadedSegments) {
      const currentSegments = Array.from(segments);
      for (const segmentNumber of currentSegments) {
        if (!validSegments.has(segmentNumber)) {
          segments.delete(segmentNumber);
        }
      }
    }
  }
}
